import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CardDisclosure } from '@components/card-disclosure';
import { CRITICAL, WARNING } from '@components/card-disclosure/src/card-disclosure.type';
import { IconWarning } from '@components/icons';
import { LabelInfo } from '@components/label/src/label-info';
import { ModalFooter } from '@components/modals/modal-footer';
import { ModalHeader } from '@components/modals/modal-header';
import {
  ImportCSVErrorMessage,
  ImportCSVQrCodeIssues,
  ImportCSVReviewForm,
  ReviewImportProps,
} from '@components/process-import-csv/src/process-import-csv.type';
import { SM_VIEWPORT } from '@constants/viewport';
import { useWindowSize } from '@hooks/use-window-size';

import { createQrCodeIssueLists } from '../utils/create-qr-code-issue-lists';
import { formatCsvFormToBackend } from '../utils/format-csv-form-to-backend';
import { bookingFields, createGeneralErrorsMessage, validateFields } from '../utils/validate-import-csv';

import { CsvColumns } from './csv-columns';
import { CsvImportFormErrors } from './csv-import-form-errors';

const ReviewImport = ({ columns, onContinue, onDiscard }: ReviewImportProps) => {
  const alertBoxRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const isMobileViewPort = useWindowSize(SM_VIEWPORT);
  const [removeFirstRow, setRemoveFirstRow] = useState<boolean>(false);
  const [processedColumns] = useState<Array<string[]>>(columns);
  const [qrCodeIssues, setQrCodeIssues] = useState<ImportCSVQrCodeIssues>({
    emptyRows: [],
    duplicateRows: [],
    invalidRows: [],
  });

  const {
    control,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors: formErrors, isSubmitting },
    formState,
  } = useFormContext<ImportCSVReviewForm>();

  useFieldArray({
    control,
    name: 'columns',
  });

  const formColumnErrors = formErrors.columns;
  const selectedFields = useWatch({ control, name: 'columns' });
  const columnsToSend = useMemo(
    () => (removeFirstRow ? processedColumns.map((col) => col.slice(1)) : processedColumns),
    [removeFirstRow, processedColumns]
  );

  const setQrCodeIssuesForColumn = useCallback(
    (qrCodeIndex: number) => {
      setQrCodeIssues(createQrCodeIssueLists(columnsToSend, qrCodeIndex));
    },
    [columnsToSend]
  );

  const setColumnErrors = useCallback(
    (columnErrors: ImportCSVErrorMessage[]) =>
      columnErrors.forEach((columnError: ImportCSVErrorMessage) => {
        // Column 0 is a valid index, so only a missing columnValue (a general error) is skipped.
        const { columnValue } = columnError;
        if (columnValue === undefined) return;
        setError(`columns.${columnValue}.field`, {
          message: t(columnError.literal, {
            columnValue: columnError.columnLabel,
            message: columnError.message,
            rows: columnError.rows,
          }),
        });
      }),
    [t, setError]
  );

  const setGeneralErrors = useCallback(
    (generalErrors: Set<ImportCSVErrorMessage>) => {
      if (generalErrors.size > 0) {
        const message = createGeneralErrorsMessage(generalErrors, t);
        setError('columns', {
          message,
        });
      }
    },
    [t, setError]
  );

  const scrollToTop = useCallback(() => {
    if (alertBoxRef.current) alertBoxRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [alertBoxRef]);

  const validateFieldsCallback = useCallback(
    async (formData: { field: string }[]) => {
      clearErrors();
      const validation = validateFields({ formData, processedColumns: columnsToSend, bookingFields });
      if (!validation) return;
      const { columnErrors, generalErrors, qrCodeColumnIndex } = validation;
      setGeneralErrors(generalErrors);
      setColumnErrors(columnErrors);
      setQrCodeIssuesForColumn(qrCodeColumnIndex);
      if (columnErrors.length > 0 || generalErrors.size > 0) scrollToTop();
      const hasErrors = columnErrors.length > 0 || generalErrors.size > 0;
      return hasErrors;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      columnsToSend,
      clearErrors,
      setQrCodeIssuesForColumn,
      setColumnErrors,
      setGeneralErrors,
      removeFirstRow,
      scrollToTop,
    ]
  );

  const handleRemoveFirstRow = (shouldRemove: boolean) => {
    setRemoveFirstRow(shouldRemove);
  };

  const onSubmit = async (formData: ImportCSVReviewForm) => {
    const hasErrors = await validateFieldsCallback(formData.columns);
    if (!hasErrors) {
      const mappedData = formatCsvFormToBackend(formData, columnsToSend);
      onContinue(qrCodeIssues, mappedData);
    }
  };

  useEffect(() => {
    if (formState.isSubmitted) {
      validateFieldsCallback(selectedFields);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState.dirtyFields.columns, selectedFields, columnsToSend, removeFirstRow]);

  return (
    <>
      <ModalHeader title={t('common:reviewImportTitle')} subtitle={t('eventReport:importCsvModalSubtitle')} />
      <div className='px-4 pb-6 sm:px-8 sm:pb-12' ref={alertBoxRef}>
        <p
          className='text-body-s text-primary pb-2'
          dangerouslySetInnerHTML={{ __html: t('common:reviewImportDescription') }}
        />
        {processedColumns.length === 1 && (
          <CardDisclosure
            variant={WARNING}
            showChevronIcon={false}
            titleText={t('common:oneColumnDisclaimerTitle')}
            icon={{
              height: 'h-5',
              icon: IconWarning,
              width: 'w-5',
            }}
          />
        )}
        <div className='mb-4'>
          <label className='flex items-center'>
            <input
              id='removeFirstRow'
              type='checkbox'
              className='mr-2'
              checked={removeFirstRow}
              onChange={(e) => handleRemoveFirstRow(e.target.checked)}
            />
            <LabelInfo
              isBold={false}
              htmlFor='removeFirstRow'
              customColors='text-secondary'
              className='text-secondary text-body-s &:[>label]:font-normal'
              text={t('common:removeFirstRowTooltip')}
              marginBottomClassName='mb-0'>
              {t('common:removeFirstRow')}
            </LabelInfo>
          </label>
          {formColumnErrors && (
            <CardDisclosure
              variant={CRITICAL}
              titleText={t('errors:importCSVErrorTitle')}
              descriptionText={<CsvImportFormErrors formColumnErrors={formColumnErrors} />}
              isDefaultOpen
              marginClassNames='mt-3'
              paddingDescription='pl-7 pt-2'
              showChevronIcon
              icon={{
                height: 'h-5',
                icon: IconWarning,
                width: 'w-5',
              }}
            />
          )}
        </div>
        <div>
          <CsvColumns
            isMobileViewPort={isMobileViewPort}
            processedColumns={processedColumns}
            removeFirstRow={removeFirstRow}
          />
        </div>
      </div>
      <ModalFooter
        isPrimaryDisabled={isSubmitting || Object.keys(formErrors).length > 0}
        primaryButtonText={t('offers:continueButton')}
        onPrimaryClick={handleSubmit(onSubmit)}
        onSecondaryClick={onDiscard}
        secondaryButtonText={t('common:discard')}
      />
    </>
  );
};

export default ReviewImport;
